import type { ShoonyaAdapter } from './ShoonyaAdapter'
import type {
	IProviderWebSocketProtocol,
	ProviderDepthLevel,
	ProviderQuote,
	ProviderSecurityRef,
	ProviderWebSocketEvent,
	ProviderWebSocketFeedKind,
	ProviderWebSocketStream
} from './types'
import type {
	ShoonyaOrderBookItem,
	ShoonyaPositionBookItem,
	ShoonyaWsConnectRequest,
	ShoonyaWsDepthFields,
	ShoonyaWsMessage,
	ShoonyaWsOrderUpdate,
	ShoonyaWsPositionUpdate,
	ShoonyaWsQuoteFields,
	ShoonyaWsRequest
} from './shoonya.types'

/** Separator between scrips in the `k` field: NSE|22#BSE|508123 */
const SCRIP_LIST_SEPARATOR = '#'

const toNumber = (value: string | undefined): number | undefined => {
	if (value === undefined || value === null || value === '') return undefined
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : undefined
}

const toInt = (value: string | undefined): number => {
	const parsed = parseInt(value ?? '0', 10)
	return Number.isFinite(parsed) ? parsed : 0
}

const toFloat = (value: string | undefined): number => {
	const parsed = parseFloat(value ?? '0')
	return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Wire protocol for the Shoonya (Noren) websocket as described in
 * "Web Socket Noren Mobile Client API".
 *
 * Outgoing frames are JSON objects with a `t` task code; incoming frames are JSON
 * objects with a `t` message code. This class only builds/parses frames — the
 * socket lifecycle lives in the provider-agnostic React layer.
 */
export class ShoonyaWebSocketProtocol implements IProviderWebSocketProtocol {
	constructor(private readonly adapter: ShoonyaAdapter) {}

	private serialize(request: ShoonyaWsRequest): string {
		return JSON.stringify(request)
	}

	// -------------------------------------------------------------------
	// Outgoing frames
	// -------------------------------------------------------------------

	/** `{"t":"c", uid, actid, source, susertoken}` — sent as soon as the socket opens. */
	buildConnectMessage(): string | null {
		const { uid, actid, susertoken, source } = this.adapter.getShoonyaSessionContext()
		if (!uid || !actid || !susertoken) {
			return null
		}
		const request: ShoonyaWsConnectRequest = {
			t: 'c',
			uid,
			actid,
			source,
			susertoken
		}
		return this.serialize(request)
	}

	/** `{"t":"h"}` — server replies with `hk`. */
	buildHeartbeatMessage(): string | null {
		return this.serialize({ t: 'h' })
	}

	/** touchline → `{"t":"t","k":"NSE|22#..."}`, depth → `{"t":"d","k":"..."}` */
	buildSubscribeMessage(kind: ProviderWebSocketFeedKind, keys: string[]): string | null {
		const k = this.joinKeys(keys)
		if (!k) return null
		return this.serialize({ t: kind === 'depth' ? 'd' : 't', k })
	}

	/** touchline → `{"t":"u","k":"..."}`, depth → `{"t":"ud","k":"..."}` */
	buildUnsubscribeMessage(kind: ProviderWebSocketFeedKind, keys: string[]): string | null {
		const k = this.joinKeys(keys)
		if (!k) return null
		return this.serialize({ t: kind === 'depth' ? 'ud' : 'u', k })
	}

	/**
	 * orders    → `{"t":"om","actid":...}`, starts `om` updates
	 * positions → `{"t":"pm","actid":...}`, starts `pm` updates
	 */
	buildSubscribeStreamMessage(stream: ProviderWebSocketStream): string | null {
		const { actid } = this.adapter.getShoonyaSessionContext()
		if (!actid) return null
		return this.serialize(stream === 'orders' ? { t: 'om', actid } : { t: 'pm', actid })
	}

	/** orders → `{"t":"uo"}` (ack `uok`), positions → `{"t":"up"}` (ack `upk`) */
	buildUnsubscribeStreamMessage(stream: ProviderWebSocketStream): string | null {
		return this.serialize(stream === 'orders' ? { t: 'uo' } : { t: 'up' })
	}

	/**
	 * Subscription keys are `EXCH|TOKEN`. Tokens are learnt from the order book,
	 * position book, holdings and websocket feeds, so a security is only
	 * subscribable once the app has seen its token.
	 */
	getSubscriptionKey(security: ProviderSecurityRef): string | null {
		return this.adapter.getShoonyaSubscriptionKey(security)
	}

	private joinKeys(keys: string[]): string {
		return Array.from(new Set(keys.filter((key) => !!key))).join(SCRIP_LIST_SEPARATOR)
	}

	// -------------------------------------------------------------------
	// Incoming frames
	// -------------------------------------------------------------------

	parseMessage(raw: string): ProviderWebSocketEvent[] {
		let parsed: unknown
		try {
			parsed = JSON.parse(raw)
		} catch {
			return [{ type: 'unknown', raw }]
		}
		// A frame can occasionally carry an array of messages; normalise to a list.
		const messages = Array.isArray(parsed) ? parsed : [parsed]
		return messages.map((message) => this.toEvent(message as ShoonyaWsMessage))
	}

	private toEvent(message: ShoonyaWsMessage): ProviderWebSocketEvent {
		if (!message || typeof message !== 'object' || !('t' in message)) {
			return { type: 'unknown', raw: message }
		}

		switch (message.t) {
			case 'ck':
				// Doc says "Ok"; live servers reply "OK". Compare case-insensitively.
				return String(message.s || '').toUpperCase() === 'OK'
					? { type: 'connected', uid: message.uid }
					: { type: 'connect_rejected', reason: message.s || 'Not_Ok' }

			case 'hk':
				return { type: 'heartbeat_ack', feedTime: message.ft }

			case 'tk':
				return {
					type: 'subscription_ack',
					kind: 'touchline',
					key: this.keyOf(message),
					security: this.securityOf(message),
					quote: this.quoteOf(message),
					raw: message
				}

			case 'dk':
				return {
					type: 'subscription_ack',
					kind: 'depth',
					key: this.keyOf(message),
					security: this.securityOf(message),
					quote: this.quoteOf(message),
					raw: message
				}

			case 'tf': {
				const { bids, asks } = this.depthOf(message)
				return {
					type: 'touchline',
					key: this.keyOf(message),
					security: this.securityOf(message),
					quote: this.quoteOf(message),
					bids,
					asks,
					raw: message
				}
			}

			case 'df': {
				const { bids, asks } = this.depthOf(message)
				return {
					type: 'depth',
					key: this.keyOf(message),
					security: this.securityOf(message),
					quote: this.quoteOf(message),
					bids,
					asks,
					raw: message
				}
			}

			case 'uk':
				return { type: 'unsubscription_ack', kind: 'touchline', keys: (message.k || '').split(SCRIP_LIST_SEPARATOR).filter(Boolean) }

			case 'udk':
				return { type: 'unsubscription_ack', kind: 'depth', keys: (message.k || '').split(SCRIP_LIST_SEPARATOR).filter(Boolean) }

			case 'ok':
				return { type: 'stream_ack', stream: 'orders', subscribed: true, raw: message }

			case 'uok':
				return { type: 'stream_ack', stream: 'orders', subscribed: false, raw: message }

			case 'pk':
				return { type: 'stream_ack', stream: 'positions', subscribed: true, raw: message }

			case 'upk':
				return { type: 'stream_ack', stream: 'positions', subscribed: false, raw: message }

			case 'om':
				// `om` without an order payload is the subscription acknowledgement.
				if (!message.norenordno) {
					return { type: 'stream_ack', stream: 'orders', subscribed: true, raw: message }
				}
				return {
					type: 'order_update',
					order: this.adapter.mapShoonyaOrder(this.toOrderBookItem(message)),
					reportType: message.reporttype,
					raw: message
				}

			case 'pm':
				// `pm` without a contract payload is the subscription acknowledgement.
				if (!message.tsym && !message.token) {
					return { type: 'stream_ack', stream: 'positions', subscribed: true, raw: message }
				}
				return {
					type: 'position_update',
					position: this.adapter.mapShoonyaPosition(this.toPositionBookItem(message)),
					raw: message
				}

			case 'am':
				return { type: 'alert', message: message.dmsg ?? '', raw: message }

			case 'rm':
				return {
					type: 'admin_message',
					message: message.dmsg ?? '',
					messageType: message.msgtype,
					timestamp: message.tm,
					raw: message
				}

			case 'ms':
				return {
					type: 'market_status',
					exchange: message.e,
					status: message.mkt_s,
					marketType: message.mkt_t,
					description: message.desc,
					timestamp: message.tm,
					raw: message
				}

			default:
				return { type: 'unknown', raw: message }
		}
	}

	private keyOf(message: ShoonyaWsQuoteFields): string {
		return `${message.e}|${message.tk}`
	}

	private securityOf(message: ShoonyaWsQuoteFields): ProviderSecurityRef | null {
		const security =
			this.adapter.resolveSecurityByShoonyaToken(message.e, message.tk) ??
			(message.ts ? this.adapter.resolveSecurityByShoonyaSymbol(message.e, message.ts) : null)
		if (!security) {
			return null
		}
		this.adapter.rememberShoonyaToken(message.e, message.tk, security)
		return { exchange: security.exchange, code: security.code }
	}

	private quoteOf(message: ShoonyaWsQuoteFields & Partial<ShoonyaWsDepthFields>): ProviderQuote {
		return {
			lastTradedPrice: toNumber(message.lp),
			percentChange: toNumber(message.pc),
			volume: toNumber(message.v),
			open: toNumber(message.o),
			high: toNumber(message.h),
			low: toNumber(message.l),
			close: toNumber(message.c),
			averageTradePrice: toNumber(message.ap),
			openInterest: toNumber(message.oi),
			previousOpenInterest: toNumber(message.poi),
			totalOpenInterest: toNumber(message.toi),
			lastTradeTime: message.ltt,
			lastTradeQuantity: toNumber(message.ltq),
			totalBuyQuantity: toNumber(message.tbq),
			totalSellQuantity: toNumber(message.tsq),
			lowerCircuit: toNumber(message.lc),
			upperCircuit: toNumber(message.uc),
			high52Week: toNumber(message['52h']),
			low52Week: toNumber(message['52l']),
			feedTime: message.ft,
			tickSize: toNumber(message.ti),
			lotSize: toNumber(message.ls),
			pricePrecision: toNumber(message.pp),
			tradingSymbol: message.ts
		}
	}

	private depthOf(message: Partial<ShoonyaWsDepthFields>): { bids: ProviderDepthLevel[]; asks: ProviderDepthLevel[] } {
		const bids: ProviderDepthLevel[] = []
		const asks: ProviderDepthLevel[] = []
		const record = message as Record<string, string | undefined>
		for (let level = 1; level <= 5; level++) {
			const bidPrice = toNumber(record[`bp${level}`])
			const bidQty = toNumber(record[`bq${level}`])
			if (bidPrice !== undefined || bidQty !== undefined) {
				bids.push({ price: bidPrice ?? 0, quantity: bidQty ?? 0, orders: toNumber(record[`bo${level}`]) })
			}
			const askPrice = toNumber(record[`sp${level}`])
			const askQty = toNumber(record[`sq${level}`])
			if (askPrice !== undefined || askQty !== undefined) {
				asks.push({ price: askPrice ?? 0, quantity: askQty ?? 0, orders: toNumber(record[`so${level}`]) })
			}
		}
		return { bids, asks }
	}

	/** `om` carries the same order fields as the OrderBook REST item, with a few renames. */
	private toOrderBookItem(message: ShoonyaWsOrderUpdate): ShoonyaOrderBookItem {
		return {
			norenordno: message.norenordno,
			uid: message.uid ?? '',
			actid: message.actid ?? '',
			exch: message.exch as ShoonyaOrderBookItem['exch'],
			tsym: message.tsym,
			qty: message.qty ?? '0',
			prc: message.prc ?? '0',
			trgprc: message.trgprc ?? '0',
			dscqty: message.dscqty ?? '0',
			fillshares: message.fillshares ?? '0',
			trantype: message.trantype ?? '',
			prctyp: message.prctyp ?? '',
			ret: message.ret ?? '',
			prd: message.prd ?? message.pcode ?? '',
			status: message.status ?? '',
			exchordid: message.exchordid,
			exch_ord_id: message.exchordid,
			avgprc: message.avgprc ?? '0',
			rejreason: message.rejreason,
			remarks: message.remarks,
			token: message.token,
			// Extra WS-only context, kept for consumers that want it.
			reporttype: message.reporttype,
			tm: message.tm,
			exch_tm: message.exch_tm,
			ordenttm: message.ordenttm
		}
	}

	/**
	 * `pm` reports amounts and average prices rather than the day-average fields the
	 * PositionBook REST item carries; derive them so one mapper serves both.
	 */
	private toPositionBookItem(message: ShoonyaWsPositionUpdate): ShoonyaPositionBookItem {
		const dayBuyQty = toInt(message.daybuyqty)
		const daySellQty = toInt(message.daysellqty)
		const dayBuyAmt = toFloat(message.daybuyamt)
		const daySellAmt = toFloat(message.daysellamt)
		const dayBuyAvg = dayBuyQty > 0 ? dayBuyAmt / dayBuyQty : toFloat(message.buyavgprc)
		const daySellAvg = daySellQty > 0 ? daySellAmt / daySellQty : toFloat(message.sellavgprc)

		return {
			uid: message.uid ?? '',
			actid: message.actid ?? '',
			exch: message.exch as ShoonyaPositionBookItem['exch'],
			tsym: message.tsym,
			token: message.token,
			prd: message.prd ?? '',
			netqty: message.netqty ?? '0',
			daybuyqty: message.daybuyqty ?? '0',
			daysellqty: message.daysellqty ?? '0',
			daybuyamt: message.daybuyamt ?? '0',
			daysellamt: message.daysellamt ?? '0',
			daybuyavgprc: dayBuyAvg.toString(),
			daysellavgprc: daySellAvg.toString(),
			rpnl: message.rpnl ?? '0',
			cfbuyqty: message.cfbuyqty,
			cfsellqty: message.cfsellqty,
			cfbuyamt: message.cfbuyamt,
			cfsellamt: message.cfsellamt,
			openbuyqty: message.openbuyqty,
			opensellqty: message.opensellqty
		}
	}
}
